/** Core cart query name */
export const CART_QUERY_NAME = 'pylot_core_cart_query'

/**
 * Important things to consider:
 * 1. Make sure to include isSignedIn: Boolean! = false to the query
 *    and pass isSignedIn as true when necessary
 * 2. Position `applied_store_credit` before items `field`.
 *    This way store credit gets calculated only once and server returns the right value.
 */
export const cartFields = /* GraphQL */ `
 id
 email
 is_virtual
 free_gifts_skus
 last_payment_method
 enabled_secure_tokenization
 shipping_restrictions {
    tobacco_products_restriction_california_cities
 }
 applied_store_credit @include(if: $isSignedIn) {
    enabled
    current_balance {
        __typename
        currency
        value
    }
    applied_balance {
        __typename
        currency
        value
    }
    is_partial
 }
 items {
     id
     uid
     item_url_key
     is_out_stock
     oos_message
     __typename
     product {
         id
         name
         sku
         subtitle
         stock_status
         brand
         brand_text
         is_tobacco_product
         is_cbd_product
         thumbnail {
             label
             url
         }
         special_price
         price_range {
            __typename
            minimum_price {
                discount {
                    amount_off
                }
                regular_price {
                    value
                    currency
                }
                final_price {
                    value
                    currency
                }
            }
        }
         url_key
         is_custom_kit
         is_bulk_product
         attribute_set_name
         categories {
             name
             url_path
             breadcrumbs {
                 category_name
             }
         }
         __typename
     }
     ... on BundleCartItem {
         bundle_options {
           label
           uid
           type
           ooka_bundle_option
           values {
             quantity
             price
             label
             uid
             product {
                name
                is_tobacco_product
                is_cbd_product
                sku
                subtitle
                thumbnail {
                   label
                   url
                }
                price_range {
                   minimum_price {
                      regular_price {
                         value
                         currency
                      }
                      final_price {
                         value
                         currency
                      }
                   }
                }
             }
           }
        }
     }
     ... on ConfigurableCartItem {
         configurable_options {
             configurable_product_option_uid
             configurable_product_option_value_uid
             option_label
             value_label
         }
         configured_variant {
           stock_status
           thumbnail {
              url
           }
         }
     }
     ... on GiftCardCartItem {
         __typename
         amount {
             value
             currency
         }
         message
         recipient_email
         recipient_name
         sender_email
         sender_name
     }
     quantity
     is_annual_bundle
     prices {
         __typename
         price {
             value
         }
         row_total_including_tax {
             value
             currency
         }
         row_total {
             value
             currency
         }
         total_item_discount {
             value
             currency
         }
     }
 }

 prices {
     __typename
     grand_total {
         value
         currency
     }
     applied_taxes {
         label
         amount {
             value
             currency
         }
     }
     subtotal_including_tax {
         value
         currency
     }
     subtotal_excluding_tax {
         value
         currency
     }
     subtotal_with_discount_excluding_tax {
         value
         currency
     }
     discounts {
         amount {
             value
             currency
         }
     }
     loyalty_discount {
         value
         currency
     }
 }
 shipping_addresses {
     firstname
     lastname
     company
     street
     city
     region {
         code
         label
         region_id
     }
     postcode
     telephone
     country {
         code
         label
     }
     county
     available_shipping_methods {
         amount {
             currency
             value
         }
         available
         carrier_code
         carrier_title
         error_message
         method_code
         method_title
         price_excl_tax {
             value
             currency
         }
         price_incl_tax {
             value
             currency
         }
     }
     selected_shipping_method {
         carrier_code
         method_code
         carrier_title
         method_title
         amount {
             value
             currency
         }
         amount_incl_tax {
          value
          currency
        }
     }
 }
 billing_address {
     firstname
     lastname
     company
     street
     city
     region {
         code
         label
         region_id
     }
     postcode
     telephone
     country {
         code
         label
     }
    county
 }
 selected_payment_method {
     code
     title
 }
 available_payment_methods {
     code
     title
 }
 applied_coupons {
     code
 }
 applied_gift_cards {
     code
     current_balance {
         currency
         value
     }
     applied_balance {
         currency
         value
     }
 }
 `
export const cartFieldsOptimised = /* GraphQL */ `
  id
  email
  is_virtual
  free_gifts_skus
  last_payment_method
  enabled_secure_tokenization
  shipping_restrictions {
    tobacco_products_restriction_california_cities
  }
  applied_store_credit @include(if: $isSignedIn) {
    enabled
    current_balance {
      __typename
      currency
      value
    }
    applied_balance {
      __typename
      currency
      value
    }
  }
  items {
    id
    uid
    item_url_key
    __typename
    product {
      id
      name
      sku
      brand
      brand_text
      stock_status
      is_tobacco_product
      thumbnail {
        label
        url
      }
      special_price
      price_range {
        __typename
        minimum_price {
          discount {
            amount_off
          }
          regular_price {
            value
            currency
          }
          final_price {
            value
            currency
          }
        }
      }
      url_key
      attribute_set_name
      categories {
        name
        breadcrumbs {
          category_name
        }
      }
      __typename
      is_bulk_product
    }
    is_annual_bundle
      ... on BundleCartItem {
        bundle_options {
          label
          uid
          type
          ooka_bundle_option
          values {
            quantity
            price
            label
            uid
            product {
              name
              is_tobacco_product
              is_cbd_product
              sku
              subtitle
              thumbnail {
                label
                url
              }
              price_range {
                minimum_price {
                  regular_price {
                    value
                    currency
                  }
                  final_price {
                    value
                    currency
                  }
                }
              }
            }
          }
        }
      }
    ... on ConfigurableCartItem {
      configurable_options {
        configurable_product_option_uid
        configurable_product_option_value_uid
        option_label
        value_label
      }
      configured_variant {
        stock_status
        thumbnail {
            url
        }
      }
    }
    ... on GiftCardCartItem {
    __typename
    amount {
      value
      currency
    }
      message
      recipient_email
      recipient_name
      sender_email
      sender_name
    }
    quantity
    prices {
        __typename
    price {
        value
    }
    row_total_including_tax {
      value
      currency
    }
    row_total {
      value
      currency
    }
    total_item_discount {
      value
      currency
    }
    }
  }

  prices {
    __typename
  grand_total {
    value
    currency
  }
  subtotal_excluding_tax {
    value
    currency
  }
  subtotal_including_tax {
    value
    currency
  }
  subtotal_with_discount_excluding_tax {
    value
    currency
  }
  discounts {
    amount {
      value
      currency
    }
  }
    loyalty_discount {
      value
      currency
    }
  }

  applied_coupons {
    code
  }
  applied_gift_cards {
    code
    current_balance {
      currency
      value
    }
    applied_balance {
      currency
      value
    }
  }
`
